import type { Clock } from '@/lib/clock';

/**
 * The period every Reporting read is taken over, and the one place this app is
 * allowed to work out what day it is at the garden.
 *
 * ## Why a default exists here when the tickets screen refused to have one
 *
 * `routes/tickets/business-date.ts` reads no clock, on purpose, and says why:
 * knowing the date in Asia/Kolkata means asking a machine whose clock this app
 * deliberately does not trust. That was the right answer there because
 * `businessDate` on the tickets route is an **optional** filter — omitting it
 * means every day, which is a real and useful answer.
 *
 * The Reporting routes are not built that way. `from` and `to` are **required**
 * on `GET /v1/expenses` and on the cash book beside it, and the range "may not
 * end after the current business day". There is no omitting them. So the choice
 * is between a screen that shows nothing until somebody picks two dates, and a
 * screen that opens on a sensible period they can change — and the second is the
 * better screen for the primary read of an epic.
 *
 * **What this does and does not claim.** It reads the injected clock for one
 * purpose: to fill in a filter. It does not decide which trading day anything
 * *fell* in — that is the server's, resolved from its own clock, and the route
 * takes no date on the write at all precisely so nobody can move spending
 * between days after the fact (FR-705a). A device clock a day out here produces
 * a period a person can see in the pickers and correct, and one ending in the
 * future is refused by the API with copy of its own. Nothing is recorded wrong.
 *
 * `MAX_PERIOD_DAYS` and the two ordering rules are restated from the route's own
 * `422` — "a range that ends before it starts or carries a date the calendar
 * does not have, one longer than 366 days, or one ending after the current
 * business day". They are checked here so the common mistakes are a sentence
 * under a picker rather than a round trip, and the API remains what decides.
 *
 * FE050, FE051 and FE052 read the cash book, the profit and loss and the trading
 * summary over the same `from`/`to`. This is theirs as well.
 */

/** A `from`/`to` range, each `YYYY-MM-DD` or `''` for not yet chosen. */
export interface ReportingPeriod {
  readonly from: string;
  readonly to: string;
}

/** "The range covers at most 366 days — a whole leap year", published. */
export const MAX_PERIOD_DAYS = 366;

/** How far back a Reporting screen opens on. A month reads as "lately". */
const DEFAULT_SPAN_DAYS = 30;

export const PERIOD_BACKWARDS = 'The period ends before it starts. Check the two dates.';

export const PERIOD_ENDS_AHEAD =
  'A period cannot end after the current trading day. Nothing has been recorded past it yet.';

export const PERIOD_TOO_LONG = `A period covers at most ${String(MAX_PERIOD_DAYS)} days — a whole leap year. Ask for a shorter one.`;

/**
 * The park's own calendar date for an instant, in Asia/Kolkata (R-006).
 *
 * `Intl` with an explicit `timeZone` rather than a `Date`'s local getters: the
 * back office is a browser on somebody's desk and the terminals are machines a
 * supplier configured, and neither is guaranteed to be set to IST. `en-CA` is
 * used for one reason — it formats as `YYYY-MM-DD`, which is the shape the route
 * takes — and never for its language.
 *
 * **Takes a `Clock`**, per `lib/clock.ts`: this is a read of what time it is
 * now, which is the thing a test has to be able to pin. `new Date(ms)` parses the
 * number the clock handed over and reads nothing itself.
 */
const PARK_DAY = new Intl.DateTimeFormat('en-CA', {
  timeZone: 'Asia/Kolkata',
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
});

export const currentBusinessDate = (clock: Clock): string => PARK_DAY.format(new Date(clock()));

/** `YYYY-MM-DD` split into its three numbers, or `null` for anything else. */
const partsOf = (date: string): [number, number, number] | null => {
  const matched = /^(\d{4})-(\d{2})-(\d{2})$/.exec(date.trim());

  if (matched === null) return null;

  const [, year = '', month = '', day = ''] = matched;

  return [Number(year), Number(month) - 1, Number(day)];
};

/**
 * A calendar date a number of days away — `addDays('2026-03-02', -3)`.
 *
 * Fixed to UTC throughout, which is safe *because* nothing here is an instant: a
 * trading day is a date with no time in it, so anchoring the arithmetic to
 * midnight UTC and reading it back in UTC cannot move the day. The zone question
 * was already answered by `currentBusinessDate` above, once.
 *
 * The platform owns the month lengths and the leap years. Writing them out by
 * hand is the same bug one layer down, which `business-date.ts` says at length.
 */
export function addDays(date: string, days: number): string {
  const parts = partsOf(date);

  if (parts === null) return date;

  const [year, month, day] = parts;
  const moved = new Date(Date.UTC(year, month, day + days));

  return `${String(moved.getUTCFullYear()).padStart(4, '0')}-${String(moved.getUTCMonth() + 1).padStart(2, '0')}-${String(moved.getUTCDate()).padStart(2, '0')}`;
}

/** Days from `from` to `to` inclusive, so a single day is 1. */
function daysCovered({ from, to }: ReportingPeriod): number | null {
  const start = partsOf(from);
  const end = partsOf(to);

  if (start === null || end === null) return null;

  const startMs = Date.UTC(start[0], start[1], start[2]);
  const endMs = Date.UTC(end[0], end[1], end[2]);

  return Math.round((endMs - startMs) / 86_400_000) + 1;
}

/** The period a Reporting screen opens on: the current trading day and the month behind it. */
export function defaultPeriod(clock: Clock): ReportingPeriod {
  const to = currentBusinessDate(clock);

  return { from: addDays(to, -(DEFAULT_SPAN_DAYS - 1)), to };
}

/**
 * What is wrong with a period, or `null` where nothing is.
 *
 * **A half-filled range is not a refusal.** Somebody clearing one picker to
 * choose another passes through it, and turning the screen red mid-gesture is
 * the thing `docs/ui-conventions.md` refuses on a field that has not been
 * answered yet. The screen holds the last complete range while a new one is
 * being chosen, so nothing is sent from this state either.
 *
 * The order matters: an inverted range is checked before its length, because
 * `daysCovered` on one is negative and "at most 366 days" is the wrong sentence
 * for two dates the right way round.
 */
export function periodProblem(period: ReportingPeriod, clock: Clock): string | null {
  const days = daysCovered(period);

  if (days === null) return null;

  if (days < 1) return PERIOD_BACKWARDS;

  if (period.to > currentBusinessDate(clock)) return PERIOD_ENDS_AHEAD;

  if (days > MAX_PERIOD_DAYS) return PERIOD_TOO_LONG;

  return null;
}
